import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash, Play, Pause, Lightning } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecurringInvoices() {
  const [recurring, setRecurring] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", customer_id: "", gst_type: "cgst_sgst", frequency: "monthly", start_date: new Date().toISOString().split("T")[0], notes: "" });
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [recRes, custRes, prodRes] = await Promise.all([
        axios.get(`${API}/recurring-invoices`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
      ]);
      setRecurring(recRes.data);
      setCustomers(custRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => setItems([...items, { product_id: "", product_name: "", hsn_sac: "", quantity: 1, price: 0, gst_rate: 18, amount: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === "product_id") {
      const p = products.find((pr) => pr.id === value);
      if (p) { newItems[index].product_name = p.name; newItems[index].hsn_sac = p.hsn_sac; newItems[index].price = p.price; newItems[index].gst_rate = p.gst_rate; newItems[index].amount = newItems[index].quantity * p.price; }
    }
    if (field === "quantity" || field === "price") newItems[index].amount = newItems[index].quantity * newItems[index].price;
    setItems(newItems);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || items.length === 0) { alert("Fill all required fields"); return; }
    try {
      await axios.post(`${API}/recurring-invoices`, { ...formData, items });
      setDialogOpen(false);
      setFormData({ name: "", customer_id: "", gst_type: "cgst_sgst", frequency: "monthly", start_date: new Date().toISOString().split("T")[0], notes: "" });
      setItems([]);
      fetchData();
    } catch (error) { console.error("Error:", error); }
  };

  const handleToggle = async (id) => {
    try { await axios.put(`${API}/recurring-invoices/${id}/toggle`); fetchData(); } catch (error) { console.error("Error:", error); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this recurring invoice?")) return;
    try { await axios.delete(`${API}/recurring-invoices/${id}`); fetchData(); } catch (error) { console.error("Error:", error); }
  };

  const handleGenerate = async (id) => {
    try {
      const res = await axios.post(`${API}/recurring-invoices/${id}/generate`);
      alert(`Invoice ${res.data.invoice_no} generated!`);
      fetchData();
      navigate(`/invoices/${res.data.id}`);
    } catch (error) { console.error("Error:", error); alert("Failed to generate"); }
  };

  const freqLabel = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

  return (
    <div className="p-4 sm:p-8" data-testid="recurring-invoices-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">Recurring Invoices</h1>
          <p className="text-slate-500 text-sm">Automate invoicing for repeat clients</p>
        </div>
        <button onClick={() => setDialogOpen(true)} data-testid="create-recurring-btn" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200 flex items-center gap-2">
          <Plus size={20} weight="bold" /> Create Template
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-24 bg-slate-200 rounded"></div>)}</div>
      ) : recurring.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <img src="https://static.prod-images.emergentagent.com/jobs/c2875c4c-3209-4e71-8b15-de610df9cec1/images/6f94e3573c58a84c73fc78db7a1660d6d91c6b0e4489f8721400bcc17382d354.png" alt="No recurring" className="w-48 h-48 mx-auto mb-6 opacity-60" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">No recurring invoices</h3>
          <p className="text-slate-500 mb-6">Create a template for repeat billing</p>
          <button onClick={() => setDialogOpen(true)} className="px-6 py-3 bg-blue-600 text-white rounded-sm font-semibold hover:bg-blue-700 transition-all duration-200">Create Template</button>
        </div>
      ) : (
        <div className="space-y-4">
          {recurring.map((rec) => (
            <div key={rec.id} data-testid={`recurring-card-${rec.id}`} className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-all duration-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-slate-900">{rec.name}</h3>
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider ${rec.is_active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>
                      {rec.is_active ? "Active" : "Paused"}
                    </span>
                    <span className="px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider bg-blue-100 text-blue-800">
                      {freqLabel[rec.frequency] || rec.frequency}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Customer: <span className="font-semibold">{rec.customer_name}</span></p>
                  <p className="text-sm text-slate-500">Next invoice: <span className="font-semibold text-slate-900">{rec.next_date}</span></p>
                  <p className="text-sm text-slate-500">{rec.items?.length || 0} items | {rec.gst_type === "cgst_sgst" ? "CGST+SGST" : "IGST"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleGenerate(rec.id)} data-testid={`generate-invoice-${rec.id}`} className="px-3 py-2 bg-blue-600 text-white rounded-sm text-xs font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center gap-1" title="Generate Invoice Now">
                    <Lightning size={16} weight="fill" /> Generate
                  </button>
                  <button onClick={() => handleToggle(rec.id)} data-testid={`toggle-recurring-${rec.id}`} className={`p-2 rounded-sm transition-all duration-200 ${rec.is_active ? "text-amber-600 hover:bg-amber-50" : "text-green-600 hover:bg-green-50"}`} title={rec.is_active ? "Pause" : "Resume"}>
                    {rec.is_active ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                  <button onClick={() => handleDelete(rec.id)} data-testid={`delete-recurring-${rec.id}`} className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm transition-all duration-200">
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Create Recurring Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Template Name *</Label><Input data-testid="recurring-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Monthly Hosting Fee" required /></div>
              <div>
                <Label>Customer *</Label>
                <select data-testid="recurring-customer-select" value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" required>
                  <option value="">Select Customer</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Frequency *</Label>
                <select data-testid="recurring-frequency-select" value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" required>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div><Label>Start Date *</Label><Input type="date" data-testid="recurring-start-date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required /></div>
              <div>
                <Label>GST Type *</Label>
                <select data-testid="recurring-gst-type" value={formData.gst_type} onChange={(e) => setFormData({ ...formData, gst_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" required>
                  <option value="cgst_sgst">CGST + SGST</option>
                  <option value="igst">IGST</option>
                </select>
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Items</Label>
                <button type="button" onClick={addItem} data-testid="recurring-add-item" className="px-2 py-1 bg-blue-600 text-white rounded-sm text-xs font-semibold hover:bg-blue-700 flex items-center gap-1">
                  <Plus size={14} /> Add
                </button>
              </div>
              {items.map((item, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <select value={item.product_id} onChange={(e) => updateItem(index, "product_id", e.target.value)} className="flex-1 px-2 py-1.5 border border-slate-200 rounded-sm text-sm" required>
                    <option value="">Product</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <Input type="number" className="w-20" step="0.01" min="0.01" value={item.quantity} onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)} placeholder="Qty" />
                  <span className="text-sm text-slate-500 min-w-[80px] text-right">{"\u20b9"}{item.amount.toFixed(2)}</span>
                  <button type="button" onClick={() => removeItem(index)} className="p-1 text-red-500 hover:bg-red-50 rounded-sm"><Trash size={16} /></button>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Add at least one item</p>}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-recurring-btn">Create Template</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
